import React, { useState, useCallback, useRef, useMemo } from "react";
import { navigate } from "gatsby-link";
import FormInput from "./elements/form-input";
import FormPrivacy from "./elements/form-privacy";
import { getPrivacyData, submitData } from "../utility/forms";
import { validateEmail } from "../utility/formatters";
import { GroupProvider } from "../context/group";
import AltchaWidget from "../utility/altcha";

const FormNewsletter = ({ data, labelManagerLabels, location }) => {
  const emailQs = new URLSearchParams(location.search).get("email");
  const [formData, setFormData] = useState(() => ({
    type: "Newsletter subscription",
    firstName: "",
    lastName: "",
    email: emailQs ? decodeURIComponent(emailQs) : "",
    ...getPrivacyData(data),
  }));
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const altchaRef = useRef();

  const formDataRef = useRef({});
  formDataRef.current = formData;

  const onChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
    setFormErrors((current) => (current[name] ? { ...current, [name]: false } : current));
  }, []);

  const formInfo = useMemo(
    () => ({
      formData,
      onChange,
      formErrors,
      setFormErrors,
    }),
    [formData, formErrors, onChange]
  );

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const form = e.currentTarget;
      const formElements = [...form.elements];

      const missingFields = formElements.filter(
        (field) =>
          field.getAttribute &&
          field.getAttribute("data-required") === "true" &&
          (!field.value || (field.type === "checkbox" && !field.checked))
      );

      const invalidFields = [];
      if (form.email?.value && !validateEmail(form.email.value)) {
        invalidFields.push("email");
      }

      if (missingFields.length || invalidFields.length) {
        const missingErrs = missingFields.reduce(
          (res, field) => ({
            ...res,
            [field.name]: labelManagerLabels.requiredFields,
          }),
          {}
        );
        const errors = invalidFields.reduce(
          (res, field) => ({ ...res, [field]: true }),
          missingErrs
        );
        setFormErrors((current) => ({ ...current, ...errors }));

        const firstElError = formElements.find(
          (el) =>
            missingFields.some((field) => el.name === field.name) || invalidFields.includes(el.name)
        );
        var headerOffset =
          document.getElementsByClassName("header")[0].getBoundingClientRect().height + 50;
        var elementPosition = firstElError.getBoundingClientRect().top;
        var offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          behavior: "smooth",
          top: offsetPosition,
        });
        return;
      }

      setLoading(true);
      setServerError(false);

      try {
        const result = await submitData(
          {
            ...formDataRef.current,
          },
          captcha,
          data.privacyText,
          "form2"
        );
        console.log("result: ", result);
        if (data.retUrl) {
          const slug = data.retUrl;
          navigate(slug.startsWith("/") ? slug : `/${slug}`);
        } else {
          navigate("/");
        }
      } catch (err) {
        console.error("Errore durante l'invio:", err);
        setServerError(true);
        altchaRef.current?.reset();
        setCaptcha(null);
      }

      setLoading(false);
    },
    [data.privacyText, data.retUrl, labelManagerLabels, captcha]
  );

  return (
    <section className="section newsletter">
      <div className="container">
        <div className="row">
          <div className="col-12 px-0">
            <div className="newsletter__container">
              <form className="form form--newsletter" onSubmit={onSubmit}>
                <GroupProvider data={{ ...formInfo, onChange }}>
                  <FormInput
                    id="firstName"
                    field="firstName"
                    maxLength={40}
                    isRequired={true}
                    label={`${labelManagerLabels.formName} *`}
                    placeholder={labelManagerLabels.formName}
                  />
                  <FormInput
                    id="lastName"
                    field="lastName"
                    maxLength={80}
                    isRequired={true}
                    label={`${labelManagerLabels.formLastName} *`}
                    placeholder={labelManagerLabels.formLastName}
                  />
                  <FormInput
                    id="email"
                    field="email"
                    type="email"
                    maxLength={80}
                    label={`${labelManagerLabels.formEmail} *`}
                    placeholder={`${labelManagerLabels.formEmail} *`}
                    isRequired={true}
                    validate={validateEmail}
                    formatWarning={labelManagerLabels.validEmail}
                  />

                  <FormPrivacy data={data.privacyText} labelManagerLabels={labelManagerLabels} />
                  <div className="my-5">
                    <AltchaWidget ref={altchaRef} onChange={setCaptcha} />
                  </div>

                  <div className="text-center">
                    <button className="btn-link button primary" disabled={!captcha || loading}>
                      {labelManagerLabels.send}
                    </button>
                    {serverError && (
                      <p className="mt-2 form__service-not-available">
                        {labelManagerLabels.serviceNotAvailable}
                      </p>
                    )}
                  </div>
                  <div className="form__info mt-4">* {labelManagerLabels.requiredFields}</div>
                </GroupProvider>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FormNewsletter;
